// ── Compare constants & helpers ──

const COMPARE_STORAGE_KEY = "gng_compare_products";
const COMPARE_STORAGE_EVENT = "gng_compare_update";

export const MAX_COMPARE_PRODUCTS = 3;

export type CompareStoredProduct = {
  id: number;
  name: string;
  thumbnail: string | null;
  slug: string | null;
};

function parseStoredProducts(raw: string | null): CompareStoredProduct[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    const result: CompareStoredProduct[] = [];

    for (const item of parsed) {
      if (!item || typeof item !== "object") {
        continue;
      }

      const candidate = item as Partial<CompareStoredProduct>;
      if (!candidate.id || !candidate.name) {
        continue;
      }

      result.push({
        id: Number(candidate.id),
        name: String(candidate.name),
        thumbnail: candidate.thumbnail ?? null,
        slug: candidate.slug ?? null,
      });
    }

    return result.slice(0, MAX_COMPARE_PRODUCTS);
  } catch {
    return [];
  }
}

function writeCompareProducts(products: CompareStoredProduct[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(products));
  window.dispatchEvent(new CustomEvent(COMPARE_STORAGE_EVENT));
}

/** Read compare products from localStorage */
export function readCompareProducts(): CompareStoredProduct[] {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(COMPARE_STORAGE_KEY);
  return parseStoredProducts(raw);
}

/**
 * Upsert (add or update) a product into the compare list.
 * Returns `{ saved: true }` on success, or `{ saved: false }` if the list is full.
 */
export function upsertCompareProduct(
  product: CompareStoredProduct,
): { saved: boolean } {
  const current = readCompareProducts();

  // If product already exists, update its data in place
  const existingIndex = current.findIndex((item) => item.id === product.id);
  if (existingIndex !== -1) {
    current[existingIndex] = { ...current[existingIndex], ...product };
    writeCompareProducts(current);
    return { saved: true };
  }

  // If list is full, remove the oldest selected product first.
  if (current.length >= MAX_COMPARE_PRODUCTS) {
    current.shift();
  }

  current.push(product);
  writeCompareProducts(current);
  return { saved: true };
}

/** Remove a product from the compare list by id */
export function removeCompareProduct(id: number): void {
  const current = readCompareProducts();
  const filtered = current.filter((item) => item.id !== id);
  writeCompareProducts(filtered);
}
